// exp_PORTFOLIO-61587_file
import { FC, Ref, useMemo, useState } from 'react';

import { useStaticDataProvider } from '@hh.ru/magritte-common-data-provider';
import {
    Button,
    ChipsInput,
    ChipsTriggerState,
    CustomChip,
    Flex,
    NavBar,
    SearchInput,
    Select,
    Text,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, PlusOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

interface SelectOption {
    value: string;
    text: string;
}

interface MultiSelectFieldProps {
    title: string;
    placeholder: string;
    errorMessage: string;
    invalid: boolean;
    options: SelectOption[];
    selected: number[];
    onChange: (ids: number[]) => void;
    onBlur: () => void;
    dataQa: string;
    selectLabel: string;
    applyLabel: string;
    resetLabel: string;
    searchPlaceholder: string;
    emptyTitle: string;
    emptyDescription: string;
}

export const MultiSelectField: FC<MultiSelectFieldProps> = ({
    title,
    placeholder,
    errorMessage,
    invalid,
    options,
    selected,
    onChange,
    onBlur,
    dataQa,
    selectLabel,
    applyLabel,
    resetLabel,
    searchPlaceholder,
    emptyTitle,
    emptyDescription,
}) => {
    const [inputValue, setInputValue] = useState('');

    const { isMobile } = useBreakpoint();

    const dataProvider = useStaticDataProvider(options);
    const nameById = useMemo(() => new Map(options.map(({ value, text }) => [value, text])), [options]);

    const chipsState: ChipsTriggerState = useMemo(
        () => ({
            value: inputValue,
            chips: selected.map((id) => ({
                value: String(id),
                label: nameById.get(String(id)) ?? String(id),
            })),
        }),
        [inputValue, nameById, selected]
    );

    const handleDesktopChange = (newState: ChipsTriggerState) => {
        setInputValue(newState.value);
        onChange(newState.chips.map((chip) => Number(chip.value)));
    };

    const selectedOptions = useMemo(
        () =>
            selected.flatMap((id) => {
                const text = nameById.get(String(id));
                return text ? [{ value: String(id), text }] : [];
            }),
        [nameById, selected]
    );

    const removeSelected = (id: number) => {
        onChange(selected.filter((item) => item !== id));
    };

    return (
        <div>
            <Text typography="label-2-regular">{title}</Text>
            <VSpacing default={12} />
            {isMobile ? (
                <Select
                    type="checkbox"
                    multiple
                    searchable
                    name={dataQa}
                    value={selectedOptions}
                    dataProvider={dataProvider}
                    headerTitle={title}
                    bottomSheetHeight="full-screen"
                    applyOnClear={false}
                    onChange={(values) => onChange(values.map((option) => Number(option.value)))}
                    onBlur={onBlur}
                    emptyOptionsSearchHint={
                        <Flex direction="column" align="center" gap={8}>
                            <Text typography="title-5-semibold">{emptyTitle}</Text>
                            <Text style="secondary">{emptyDescription}</Text>
                        </Flex>
                    }
                    applyChangesButton={
                        <Button mode="primary" style="accent" stretched data-qa={`${dataQa}-apply`}>
                            {applyLabel}
                        </Button>
                    }
                    clearButton={
                        <Button mode="secondary" style="accent" stretched data-qa={`${dataQa}-reset`}>
                            {resetLabel}
                        </Button>
                    }
                    renderHeader={({
                        onCloseBottomSheet,
                        searchValue,
                        setSearchValue,
                        searchable,
                        onSearchSubmit,
                        optionsCount,
                        onCloseBottomSheetWithUpdateValues,
                    }) => (
                        <NavBar>
                            <NavBar.Pane>
                                <NavBar.Actions
                                    centered
                                    right={
                                        <CrossOutlinedSize24 initialColor="secondary" onClick={onCloseBottomSheet} />
                                    }
                                >
                                    <NavBar.TitleContainer centered title={title} />
                                </NavBar.Actions>
                                {searchable && (
                                    <>
                                        <VSpacing default={16} />
                                        <SearchInput
                                            clearable
                                            size="medium"
                                            value={searchValue}
                                            onChange={setSearchValue}
                                            onKeyDown={(event) => {
                                                if (onSearchSubmit && event.key === 'Enter') {
                                                    onSearchSubmit(
                                                        event,
                                                        onCloseBottomSheet,
                                                        optionsCount,
                                                        onCloseBottomSheetWithUpdateValues
                                                    );
                                                }
                                            }}
                                            placeholder={searchPlaceholder}
                                            data-qa={`${dataQa}-search`}
                                        />
                                        <VSpacing default={16} />
                                    </>
                                )}
                            </NavBar.Pane>
                        </NavBar>
                    )}
                    renderTrigger={({ ref, onChange: togglePicker }) => (
                        <div ref={ref as Ref<HTMLDivElement>}>
                            <VSpacingContainer default={12}>
                                {selected.length === 0 && <Text style="secondary">{placeholder}</Text>}
                                <Flex gap={8} wrap="wrap">
                                    {selected.map((id) => (
                                        <CustomChip
                                            key={id}
                                            Element="button"
                                            size="extra-small"
                                            deleteOnly
                                            onDelete={() => removeSelected(id)}
                                            data-qa={`${dataQa}-chip-${id}`}
                                        >
                                            {nameById.get(String(id)) ?? String(id)}
                                        </CustomChip>
                                    ))}
                                    <CustomChip
                                        Element="button"
                                        addon={<PlusOutlinedSize16 />}
                                        style="accent"
                                        size="extra-small"
                                        onClick={() => togglePicker(true)}
                                        data-qa={`${dataQa}-select`}
                                    >
                                        {selectLabel}
                                    </CustomChip>
                                </Flex>
                                {invalid && (
                                    <Text style="negative" data-qa={`${dataQa}-error`}>
                                        {errorMessage}
                                    </Text>
                                )}
                            </VSpacingContainer>
                        </div>
                    )}
                />
            ) : (
                <ChipsInput
                    type="select"
                    state={chipsState}
                    onChange={handleDesktopChange}
                    triggerProps={{
                        size: 'medium',
                        placeholder,
                        invalid,
                        errorMessage: invalid ? errorMessage : undefined,
                        onBlur,
                        'data-qa': dataQa,
                    }}
                    selectProps={{
                        type: 'checkbox',
                        name: dataQa,
                        dataProvider,
                    }}
                />
            )}
        </div>
    );
};
